import { SubmitButtonProps } from 'components/common/submitButton'
import React from 'react'
import { Form } from 'react-final-form'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { validateAWSEmail } from 'appsync-scalar-utils'
import moment, { Moment } from 'moment'
import { Trans, useTranslation } from 'react-i18next'
import SelectField, { SelectValue } from '../components/selectField'
import { Grid } from '@material-ui/core'
import EmailField from '../components/emailField'
import PhoneField from '../components/phoneField'
import DateField from '../components/dateField'
import TimeField from '../components/timeField'
import TextField from '../components/textField'
import CheckboxField from '../components/checkboxField'

interface Props {
    subscription?: { [property: string]: boolean }
    onSubmit: (values: BookingFormState) => void
    children: (content: React.ReactNode, actions: Array<SubmitButtonProps>) => React.ReactNode
    inProgress: boolean
    initialValues: BookingFormState
    withMinDate?: boolean
    withSendEmail?: boolean
}

export interface BookingFormState {
    givenName: string
    familyName: string
    email: string
    phone: string
    comment: string
    date: Moment
    time: Moment
    guests: number
    sendEmail: boolean
}

export interface BookingFormStateInternal {
    givenName: string
    familyName: string
    email: string
    phone: string
    comment: string
    date: Moment
    time: Moment
    guests: number
    sendEmail: boolean[]
}

interface BookingFormError {
    givenName?: string
    familyName?: string
    email?: string
    phone?: string
    comment?: string
    date?: string
    time?: string
    guests?: string
    sendEmail?: string
}

const BookingForm: React.FC<Props> = ({
    subscription = {
        submitting: true,
        pristine: true,
    },
    onSubmit,
    children,
    inProgress,
    initialValues,
    withMinDate = true,
    withSendEmail = false,
}) => {
    const { t } = useTranslation()
    const initialValuesInternal: BookingFormStateInternal = {
        ...initialValues,
        sendEmail: initialValues.sendEmail ? [true] : [],
    }
    const validate = (values: BookingFormStateInternal) => {
        const errors: BookingFormError = {}

        if (!values.givenName) {
            errors.givenName = t('form-validation.givenName')
        } else if (values.givenName.length < 2) {
            errors.givenName = t('form-validation.givenNameLength')
        }

        if (!values.familyName) {
            errors.familyName = t('form-validation.familyName')
        } else if (values.familyName.length < 2) {
            errors.familyName = t('form-validation.familyNameLength')
        }

        if (!values.email) {
            errors.email = t('form-validation.email')
        } else if (!validateAWSEmail(values.email)) {
            errors.email = t('form-validation.emailValid')
        }

        if (!values.phone) {
            errors.phone = t('form-validation.phone')
        } else if (values.phone !== '') {
            const phone = parsePhoneNumberFromString(values.phone, 'FR')
            if (phone === undefined || !phone.isValid()) {
                errors.phone = t('form-validation.phone')
            }
        }
        return errors
    }

    const guestsPossibilities: Array<SelectValue> = []
    for (let i = 1; i <= 30; i++) {
        guestsPossibilities.push({
            key: 'guestsPossibilities' + i,
            label: i === 1 ? t('form.guestsNumberOne') : t('form.guestsNumber', { number: i }),
            value: i,
        })
    }

    const handleSubmit = (values: BookingFormStateInternal) => {
        return onSubmit({
            ...values,
            sendEmail: values.sendEmail && values.sendEmail.length > 0,
        })
    }
    return (
        <Form
            initialValues={initialValuesInternal}
            subscription={subscription}
            // debug={console.log}
            onSubmit={handleSubmit}
            validate={validate}
            keepDirtyOnReinitialize
        >
            {({ handleSubmit, submitting }) => {
                return (
                    <form onSubmit={handleSubmit} noValidate autoComplete="off">
                        {children(
                            <>
                                <Grid container spacing={2} justifyContent="flex-end">
                                    <Grid item sm={6} xs={12}>
                                        <TextField
                                            margin="none"
                                            name="givenName"
                                            label={t('form.firstName')}
                                            autoComplete="given-name"
                                            required
                                        />
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <TextField
                                            margin="none"
                                            name="familyName"
                                            label={t('form.lastName')}
                                            autoComplete="family-name"
                                            required
                                        />
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <EmailField
                                            margin="none"
                                            label={t('form.email')}
                                            name="email"
                                            required
                                        />
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <PhoneField
                                            margin="none"
                                            label={t('form.phoneNumber')}
                                            name="phone"
                                            required
                                        />
                                    </Grid>
                                    <Grid item sm={4} xs={12}>
                                        <SelectField
                                            margin="none"
                                            name="guests"
                                            label={t('form.guests')}
                                            possibilities={guestsPossibilities}
                                            required
                                        />
                                    </Grid>
                                    <Grid item sm={4} xs={12}>
                                        <DateField
                                            minDate={withMinDate ? moment() : undefined}
                                            margin="none"
                                            name="date"
                                            label={t('form.date')}
                                            required
                                        />
                                    </Grid>
                                    <Grid item sm={4} xs={12}>
                                        <TimeField
                                            margin="none"
                                            name="time"
                                            label={t('form.time')}
                                            minutesStep={5}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            margin="none"
                                            name="comment"
                                            multiline
                                            minRows={4}
                                            label={t('form.comment')}
                                        />
                                    </Grid>
                                    {withSendEmail && (
                                        <Grid item lg={4} md={6} xs={8}>
                                            <CheckboxField
                                                name="sendEmail"
                                                value={true}
                                                label={t('form.sendCustomerEmail')}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </>,
                            [
                                {
                                    type: 'submit',
                                    loading: submitting || inProgress,
                                    children: <Trans i18nKey="actions.book" />,
                                },
                            ],
                        )}
                    </form>
                )
            }}
        </Form>
    )
}

export default BookingForm
